/**
 * The Forma chart's recorded sessions: an ember point on each session a recording is anchored to.
 * A double click, or Enter on the focused point, puts its recording in the side panel
 * (`recordedPointShows`); A and B stay. A hover shows its poster frame.
 *
 * Real buttons in an HTML layer over the plot, like the A and B grips, so each has a name and a
 * keyboard path. They carry `data-plot-control`, so the plot's own listener leaves them to this.
 */

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { useChartStable } from '~/shared/ui/chart/chartOverlay';
import photo from '~/shared/ui/photoBox.module.css';

import { recordedPointShows, type FormaRecordedPoint } from '../model/formaView';

import styles from './Forma.module.css';

/** The tooltip's own width, so it can be kept inside the plot at either end. */
const TIP_WIDTH = 220;

interface RecordedPointsProps {
  readonly points: readonly FormaRecordedPoint[];
  readonly onShowInPanel: (point: FormaRecordedPoint) => void;
}

export function RecordedPoints({ points, onShowInPanel }: RecordedPointsProps) {
  const { t } = useTranslation();
  const { xScale, yScale, margin, innerWidth, containerRef } = useChartStable();
  const [box, setBox] = useState<HTMLElement | null>(null);
  const [hovered, setHovered] = useState<number | null>(null);
  useEffect(() => {
    setBox(containerRef.current);
  }, [containerRef]);

  if (box === null || innerWidth <= 0) return null;
  const tip = points.find((point) => point.clipId === hovered) ?? null;
  const xOf = (point: FormaRecordedPoint) => margin.left + (xScale(point.at) ?? 0);

  const stop = (event: { stopPropagation: () => void }) => {
    event.stopPropagation();
  };

  return createPortal(
    <div className={styles.recordLayer} onMouseMove={stop} onMouseEnter={stop} onMouseLeave={stop}>
      {points.map((point) => (
        <button
          aria-label={t('web:forma.stage.openRecorded', { date: point.dateLong })}
          className={styles.recordPoint}
          data-plot-control=""
          key={point.clipId}
          onBlur={() => {
            setHovered(null);
          }}
          onClick={(event) => {
            if (recordedPointShows(event.detail)) onShowInPanel(point);
          }}
          onFocus={() => {
            setHovered(point.clipId);
          }}
          onMouseEnter={() => {
            setHovered(point.clipId);
          }}
          onMouseLeave={() => {
            setHovered(null);
          }}
          style={{
            insetInlineStart: `${String(xOf(point))}px`,
            insetBlockStart: `${String(margin.top + (yScale(point.value) ?? 0))}px`,
          }}
          type="button"
        />
      ))}
      {tip === null ? null : (
        <div
          aria-hidden="true"
          className={styles.recordTip}
          style={{
            insetInlineStart: `${String(
              Math.min(
                Math.max(xOf(tip) - TIP_WIDTH / 2, margin.left),
                margin.left + innerWidth - TIP_WIDTH,
              ),
            )}px`,
          }}
        >
          {tip.posterUrl === null ? (
            <span className={styles.recordTipPoster} />
          ) : (
            <img
              alt=""
              className={`${String(photo.photo)} ${String(styles.recordTipPoster)}`}
              decoding="async"
              src={tip.posterUrl}
            />
          )}
          <span className={styles.recordTipText}>
            <span className={styles.recordTipDate}>{tip.dateLong}</span>
            <b className={styles.recordTipValue}>{tip.valueLabel}</b>
            {tip.topSet === null ? null : <span className={styles.recordTipNote}>{tip.topSet}</span>}
            <span className={styles.recordTipHint}>{t('web:forma.stage.recordedHint')}</span>
          </span>
        </div>
      )}
    </div>,
    box,
  );
}

RecordedPoints.displayName = 'RecordedPoints';
(RecordedPoints as { __isPostOverlay?: boolean }).__isPostOverlay = true;
